import { Fragment, useCallback, useEffect, useState } from 'react';

import { convertSellerRole } from 'utils/convertRole';
import { firstLetterUppercase, formatCPF } from 'utils/formatters';
import { ISeller } from 'v3/data/store/seller/types';
import {
  EditOutlinedIconStyle,
  IconAlignment,
  SwitchCommonPosition,
} from 'v3/shared/components/MobileTable/SellerMobileTable/styles';
import * as api from 'v3/services/api';
import { SwitchCommon } from 'v3/shared/components/Switch';
import { FeatureFlagEnum } from 'v3/shared/enums/feature-flag.enum';
import { SubtitleSmallBold } from 'v3/shared/typography/SubtitleSmall';
import { theme } from 'v3/styles/global-styles';
import { AlignClientInfo, AlignValues, BodyLine, HeaderLine, MainContainer } from '../styles';
import { SellerContactIcons } from 'v3/shared/components/SellerContactIcons';

interface IMobileTable {
  sellerList: ISeller[];
  getFunction: (seller: ISeller, event: React.ChangeEvent<HTMLInputElement>) => void;
  updateSeller: (seller) => void;
}

export const SellerMobileTable: React.FC<IMobileTable> = ({ sellerList, getFunction, updateSeller }) => {
  const [sellerBankAccountFeatureEnabled, setSellerBankAccountFeatureEnabled] = useState(false);

  const handleOpenEditSellerModal = (seller) => {
    updateSeller(seller);
  };

  const handleSwitchChange = (seller: ISeller, event: React.ChangeEvent<HTMLInputElement>) => {
    getFunction(seller, event);
  };

  const fetchSellerFeatureFlag = useCallback(async () => {
    const featureBackofficeResponse = await api.getFlagStatus(
      FeatureFlagEnum.IKCLOUD_12852_SELLER_BANK_ACCOUNT_BACKOFFICE,
    );
    const featurePhysicalResponse = await api.getFlagStatus(FeatureFlagEnum.IKCLOUD_12852_SELLER_BANK_ACCOUNT_PHYSICAL);

    const isFeatureEnabled = featureBackofficeResponse.data.enabled || featurePhysicalResponse.data.enabled;

    setSellerBankAccountFeatureEnabled(isFeatureEnabled);
  }, []);

  useEffect(() => {
    fetchSellerFeatureFlag();
  }, []);

  return (
    <Fragment>
      {sellerList.map((seller, index) => (
        <MainContainer key={index}>
          <AlignValues>
            <HeaderLine>Nome</HeaderLine>
            <AlignClientInfo>
              <BodyLine>{firstLetterUppercase(seller.name)}</BodyLine>
              <BodyLine>{formatCPF(seller.code)}</BodyLine>

              {sellerBankAccountFeatureEnabled ? (
                <BodyLine>
                  <SellerContactIcons
                    phoneAreaCode={seller.phoneAreaCode}
                    phoneNumber={seller.phoneNumber}
                    email={seller.email}
                  />
                </BodyLine>
              ) : null}
            </AlignClientInfo>
          </AlignValues>

          <AlignValues>
            <HeaderLine>Loja</HeaderLine>
            <AlignClientInfo>
              <BodyLine>{firstLetterUppercase(seller.store?.name)}</BodyLine>
            </AlignClientInfo>
          </AlignValues>

          {sellerBankAccountFeatureEnabled ? (
            <Fragment>
              <AlignValues>
                <HeaderLine>Chave Pix</HeaderLine>
                <AlignClientInfo>
                  <BodyLine>
                    <SubtitleSmallBold color={theme.palette.common.white}>{seller.pixKey ?? '-'}</SubtitleSmallBold>
                  </BodyLine>
                </AlignClientInfo>
              </AlignValues>

              <AlignValues>
                <HeaderLine>Cargo</HeaderLine>
                <AlignClientInfo>
                  <BodyLine>{convertSellerRole(seller.role)}</BodyLine>
                </AlignClientInfo>
              </AlignValues>
            </Fragment>
          ) : null}

          <AlignValues>
            <HeaderLine>Ações</HeaderLine>
            <AlignClientInfo>
              <IconAlignment>
                <BodyLine>{'Editar'}</BodyLine>
                <EditOutlinedIconStyle onClick={() => handleOpenEditSellerModal(seller)} />
              </IconAlignment>

              <SwitchCommonPosition>
                <SwitchCommon
                  checked={seller.active}
                  onChange={(event) => {
                    handleSwitchChange(seller, event);
                  }}
                />
              </SwitchCommonPosition>
            </AlignClientInfo>
          </AlignValues>
        </MainContainer>
      ))}
    </Fragment>
  );
};
